import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSessionStorage } from "../../hooks/useSessionStorage";

export interface QuizEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  localeAwareBaseUrl?: string;
  onSend?: (payload: {
    email: string;
    firstName: string;
    lastName: string;
    emailOptIn: boolean;
  }) => Promise<void>;
}

export const QuizEmailModal: React.FC<QuizEmailModalProps> = ({
  isOpen,
  onClose,
  localeAwareBaseUrl = "",
  onSend,
}) => {
  const [email, setEmail] = useSessionStorage("emailAddress", "");
  const [firstName, setFirstName] = useSessionStorage("firstName", "");
  const [lastName, setLastName] = useSessionStorage("lastName", "");
  const [emailOptIn, setEmailOptIn] = useState(true);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSend = async () => {
    if (!onSend) return;
    setSending(true);
    try {
      await onSend({ email, firstName, lastName, emailOptIn });
      setSent(true);
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    setSending(false);
    setSent(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className={`quiz-modal quiz-modal--send-email quiz ${isOpen ? "showing" : "hidden"}`}
    >
      <a
        href="#close"
        className="quiz-modal-link"
        onClick={(e) => {
          e.preventDefault();
          handleClose();
        }}
        aria-hidden
      />
      <div className="quiz-modal-overlay">
        <motion.div
          className="quiz-modal-dialog"
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.96 }}
          transition={{ duration: 0.2 }}
        >
          <a
            href="#close"
            className="quiz-modal-close"
            onClick={(e) => {
              e.preventDefault();
              handleClose();
            }}
            aria-label="Close"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M13.295 2.115C13.6844 1.72564 13.6844 1.09436 13.295 0.705L7 5.59L2.115 0.705C1.72564 0.315639 1.09436 0.315639 0.705 0.705C0.315639 1.09436 0.315639 1.72564 0.705 2.115L5.59 7L0.705 11.885C0.315639 12.2744 0.315639 12.9056 0.705 13.295C1.09436 13.6844 1.72564 13.6844 2.115 13.295L7 8.41L11.885 13.295C12.2744 13.6844 12.9056 13.6844 13.295 13.295C13.6844 12.9056 13.6844 12.2744 13.295 11.885L8.41 7L13.295 2.115Z"
                fill="#5E5E5E"
              />
            </svg>
          </a>

          <AnimatePresence mode="wait">
            {sending && (
              <motion.div
                key="sending"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <h4 className="quiz-modal-title h3">Sending...</h4>
                <p className="quiz-modal-content">
                  We're sending your results now. This will take a few minutes.
                </p>
              </motion.div>
            )}
            {sent && (
              <motion.div
                key="sent"
                className="quiz-modal-msg-success"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <h4 className="quiz-modal-title h3">Email sent!</h4>
                <p className="quiz-modal-content">
                  <span className="deep-grey">
                    We've sent your results. If you don't see the email in a
                    couple of minutes, check your Junk folder.
                  </span>
                </p>
                <div className="quiz-modal--btn_holder">
                  <button
                    type="button"
                    className="button quiz-modal-button quiz-modal-button--short btn btn--primary"
                    onClick={handleClose}
                  >
                    Close
                  </button>
                </div>
              </motion.div>
            )}
            {!sending && !sent && (
              <motion.div
                key="form"
                className="quiz-modal--email"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <h4 className="quiz-modal-title h3">Email my quiz results</h4>
                <p className="quiz-modal-content">
                  Fill out your details to receive a copy of your results.
                </p>
                <div className="quiz-modal--email_name">
                  <div>
                    <label htmlFor="quiz-modal--email_name">First name</label>
                    <input
                      type="text"
                      id="quiz-modal--email_name"
                      name="firstName"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                    />
                  </div>
                  <div>
                    <label htmlFor="quiz-modal--email_last">Last name</label>
                    <input
                      type="text"
                      id="quiz-modal--email_last"
                      name="lastName"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                    />
                  </div>
                </div>
                <label htmlFor="quiz-modal--email_email">Email</label>
                <input
                  id="quiz-modal--email_email"
                  type="email"
                  name="email"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSend()}
                />
                <div className="quiz-modal--email_checkbox">
                  <label htmlFor="quiz-modal--email_checkbox">
                    <input
                      type="checkbox"
                      id="quiz-modal--email_checkbox"
                      name="emailOptIn"
                      checked={emailOptIn}
                      onChange={(e) => setEmailOptIn(e.target.checked)}
                    />
                    Sign me up to receive news and exclusive offers from
                    BraveFace. Selecting this option means you agree to our{" "}
                    <a
                      href="/pages/terms-and-conditions"
                      className="underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Terms of Service
                    </a>{" "}
                    and{" "}
                    <a
                      className="underline"
                      href={`${localeAwareBaseUrl}/pages/privacy-policy`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Privacy Policy
                    </a>
                    .
                  </label>
                </div>
                <div className="quiz-modal--btn_holder">
                  <button
                    type="button"
                    className="button quiz-modal-button btn btn--primary"
                    onClick={handleSend}
                  >
                    Send results
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
};
